import { PencilIcon } from "@heroicons/react/outline";
import React, { useEffect, useState } from "react";

import { InlineEdit } from "../..";
import { Badge, BadgeVariant } from "../Badge";
import { Button } from "../button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog";
import { OverflowDisplayTooltip } from "../Tooltip/OverflowDisplayTooltip";

import { TitleProps } from "./types";

import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries/folder-entities/types";

const MAX_TITLE_LENGTH = 256;

export const Title: React.FC<TitleProps> = ({
  title,
  onTitleEdit,
  badgeText,
  badgeVariant = BadgeVariant.GRAY,
  type,
  variant = "page",
}) => {
  const { isEditDisabled } = useRbac();
  const cannotEdit = isEditDisabled(type as EntityType);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [pendingTitle, setPendingTitle] = useState(title || null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  useEffect(() => {
    if (pendingTitle === null && title) {
      setPendingTitle(title);
    }
  }, [pendingTitle, title]);

  // Clear error message after a delay
  useEffect(() => {
    if (!errorMessage) {
      return;
    }
    const timer = setTimeout(() => setErrorMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  const validateTitle = (newTitle: string): string | null => {
    const trimmed = newTitle.trim();

    if (!trimmed) {
      return "Name cannot be empty.";
    }

    if (trimmed.length > MAX_TITLE_LENGTH) {
      return `Name cannot exceed ${MAX_TITLE_LENGTH} characters.`;
    }

    if (trimmed === title) {
      return "Name is unchanged.";
    }

    return null;
  };

  const handleEdit = (newTitle: string) => {
    setErrorMessage(null);

    const validationError = validateTitle(newTitle);
    if (validationError) {
      setErrorMessage(validationError);
      setPendingTitle(title);
      return;
    }

    setPendingTitle(newTitle.trim());
    setShowWarningDialog(true);
  };

  const handleConfirm = () => {
    setErrorMessage(null);
    setIsRenaming(true);
    setShowWarningDialog(false);

    onTitleEdit?.({
      newTitle: pendingTitle!,
      onSuccess: newTitle => {
        setPendingTitle(newTitle || title);
        setIsRenaming(false);
      },
      onError: () => {
        setErrorMessage("Failed to rename. Please try again.");
        setPendingTitle(title);
        setIsRenaming(false);
      },
    });
  };

  const handleCancel = () => {
    setPendingTitle(title);
    setShowWarningDialog(false);
    setErrorMessage(null);
  };

  const getTitleClasses = (titleText: string, isPending = false) => {
    const baseClasses = "truncate font-semibold";
    let sizeClasses = "text-2xl sm:text-3xl";

    if (variant === "breadcrumb") {
      sizeClasses = "text-sm";
    } else if (titleText?.length > 30) {
      sizeClasses = "text-xl sm:text-2xl";
    }

    const colorClasses = isPending && pendingTitle !== title ? "text-blue-600" : "";
    return `${baseClasses} ${sizeClasses} ${colorClasses}`.trim();
  };

  const errorDisplay = errorMessage ? (
    <p className="mt-1 text-xs text-red-600" role="alert">
      {errorMessage}
    </p>
  ) : null;

  if (cannotEdit) {
    return (
      <div
        className={
          variant === "breadcrumb"
            ? "flex min-w-0 max-w-full items-center gap-2"
            : "flex min-w-[min(12rem,100%)] max-w-full items-center gap-2 sm:max-w-[50vw]"
        }
      >
        <OverflowDisplayTooltip content={title}>
          <h1 className={getTitleClasses(title)}>{title}</h1>
        </OverflowDisplayTooltip>
        {badgeText && (
          <Badge className="inline-flex flex-shrink-0" variant={badgeVariant}>
            {badgeText}
          </Badge>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className={
          variant === "breadcrumb"
            ? "flex min-w-0 max-w-full flex-col"
            : "flex min-w-[min(12rem,100%)] max-w-full flex-col sm:max-w-[50vw]"
        }
      >
        <div className="flex min-w-0 items-center gap-2">
          <InlineEdit.Root
            initialValue={pendingTitle || ""}
            onEdit={handleEdit}
            onCancel={() => {
              setPendingTitle(title);
              setErrorMessage(null);
            }}
            readOnly={isRenaming}
            className="min-w-0 flex-1 p-0"
          >
            <InlineEdit.Trigger className="min-w-0">
              {!pendingTitle ? (
                <div className="h-6 w-48 animate-pulse rounded bg-gray-50 sm:h-8 sm:w-64" />
              ) : (
                <OverflowDisplayTooltip content={pendingTitle}>
                  <h1 className={getTitleClasses(pendingTitle, true)}>{pendingTitle}</h1>
                </OverflowDisplayTooltip>
              )}
            </InlineEdit.Trigger>
            <InlineEdit.EditTrigger>
              <PencilIcon className="h-4 w-4" />
            </InlineEdit.EditTrigger>
            <InlineEdit.Input
              className={
                variant === "breadcrumb" ? "h-7 py-0 text-sm" : "whitespace-pre-wrap py-1 text-base"
              }
            />
            <InlineEdit.Rename>Rename</InlineEdit.Rename>
            <InlineEdit.Cancel>Cancel</InlineEdit.Cancel>
          </InlineEdit.Root>
          {badgeText && (
            <Badge className="inline-flex flex-shrink-0" variant={badgeVariant}>
              {badgeText}
            </Badge>
          )}
        </div>
        {errorDisplay}
      </div>

      <Dialog open={showWarningDialog} onOpenChange={handleCancel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Warning</DialogTitle>
            <DialogDescription>
              Changing the title from &ldquo;{title}&rdquo; to &ldquo;{pendingTitle}&rdquo; may
              affect existing relationships. Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
